import { useEffect, useState } from 'react';
import { Flag, Sparkles } from 'lucide-react';
import { Badge, Button, Modal, Spinner, cn, useToast } from '../../ui';
import type { SprintCompletionSummary } from '../../features/sprintboard/insights/sprintCompletion';

export interface CompleteSprintModalProps {
  open: boolean;
  onClose: () => void;
  sprintName: string;
  /** Builds the summary WITHOUT mutating (called when the modal opens). */
  onPreview: () => Promise<SprintCompletionSummary>;
  /** Commits completion: sets status + returns carryover to the backlog. */
  onConfirm: () => Promise<SprintCompletionSummary>;
  /** Optional AI retrospective narrative over the summary. */
  onNarrate: (summary: SprintCompletionSummary) => Promise<string>;
  /** Called after a successful completion. */
  onCompleted: () => void;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'good' | 'bad';
}) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-center">
      <p
        className={cn(
          'text-lg font-semibold tabular-nums',
          tone === 'good'
            ? 'text-emerald-300'
            : tone === 'bad'
              ? 'text-red-300'
              : 'text-zinc-100',
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-zinc-500">{label}</p>
    </div>
  );
}

const ACCURACY_LABEL: Record<SprintCompletionSummary['accuracy']['bias'], string> = {
  under_estimated: 'Under-estimated',
  over_estimated: 'Over-estimated',
  accurate: 'Accurate',
  no_data: 'No data yet',
};

/**
 * Completing a sprint (§24): shows a scoreboard (Committed / Completed /
 * Carryover effort, tasks X/Y, blocked, goals advanced, estimate accuracy) plus
 * an optional Vector retrospective. Confirming sets the sprint complete and
 * returns carryover (not-DONE) tasks to the backlog.
 */
export function CompleteSprintModal({
  open,
  onClose,
  sprintName,
  onPreview,
  onConfirm,
  onNarrate,
  onCompleted,
}: CompleteSprintModalProps) {
  const { toast } = useToast();
  const [summary, setSummary] = useState<SprintCompletionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [narrating, setNarrating] = useState(false);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setNarrative(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    onPreview()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        if (!cancelled) {
          toast({
            tone: 'error',
            title: 'Couldn’t build the summary',
            description: e instanceof Error ? e.message : 'Unknown error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, onPreview, toast]);

  const runNarrate = async () => {
    if (!summary) return;
    setNarrating(true);
    try {
      setNarrative(await onNarrate(summary));
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Vector unavailable',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setNarrating(false);
    }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
      toast({
        tone: 'success',
        title: 'Sprint completed',
        description: `${sprintName} closed · carryover returned to backlog`,
      });
      onCompleted();
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t complete sprint',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setConfirming(false);
    }
  };

  const carryoverCount = summary?.carryoverTasks.length ?? 0;

  return (
    <Modal
      open={open}
      onClose={confirming ? () => undefined : onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-accent" aria-hidden="true" />
          Complete sprint ·{' '}
          <span className="font-normal text-zinc-400">{sprintName}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={confirming}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={confirming}
            disabled={loading || !summary}
            onClick={confirm}
          >
            Complete sprint
          </Button>
        </>
      }
    >
      {loading || !summary ? (
        <div className="flex items-center justify-center py-16">
          <Spinner label="Building summary" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Committed" value={`${summary.committedEffortDays}d`} />
            <Stat label="Completed" value={`${summary.completedEffortDays}d`} tone="good" />
            <Stat
              label="Carryover"
              value={`${summary.carryoverEffortDays}d`}
              tone={summary.carryoverEffortDays > 0 ? 'bad' : 'default'}
            />
            <Stat
              label="Tasks done"
              value={`${summary.tasksCompleted}/${summary.tasksTotal}`}
            />
            <Stat
              label="Blocked"
              value={String(summary.blockedTasks)}
              tone={summary.blockedTasks > 0 ? 'bad' : 'default'}
            />
            <Stat label="Goals advanced" value={String(summary.goalsAdvanced)} />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-zinc-400">Estimate accuracy:</span>
            <Badge
              tone={
                summary.accuracy.bias === 'accurate'
                  ? 'success'
                  : summary.accuracy.bias === 'no_data'
                    ? 'neutral'
                    : 'warning'
              }
            >
              {ACCURACY_LABEL[summary.accuracy.bias]}
            </Badge>
            {summary.accuracy.bias !== 'no_data' && (
              <span className="text-xs text-zinc-500">
                actual/estimate {summary.accuracy.ratio} · {summary.accuracy.sampleSize} record
                {summary.accuracy.sampleSize === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <p className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-sm text-zinc-400">
            {carryoverCount === 0
              ? 'No carryover — every task reached Done.'
              : `${carryoverCount} unfinished task${carryoverCount === 1 ? '' : 's'} will return to the backlog when you complete the sprint.`}
          </p>

          <section className="rounded-xl border border-accent/30 bg-accent-soft/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
                <Sparkles className="h-4 w-4 text-accent-fg" aria-hidden="true" />
                Vector retrospective
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Sparkles className="h-4 w-4" />}
                loading={narrating}
                onClick={runNarrate}
              >
                {narrative ? 'Regenerate' : 'Generate'}
              </Button>
            </div>
            {narrative && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">
                {narrative}
              </p>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}

export default CompleteSprintModal;
